/**
 * The donor's own corner of the home page: check-in, profile, and the organization's
 * engagement options (GH-600).
 *
 * One component owns the whole sheet because the states are one journey, not separate screens:
 *
 *   signed out            -> sign in (Apple · phone -> code) -> signed in; checked in on an event
 *   signed in, no event   -> the profile: receipts, details, sign out (+ check in, where offered)
 *   signed in, checked in -> the menu: engagement options, profile, sign out
 *
 * Everything here is optional relationship, never a wall: nothing on the giving path routes
 * through this component, and closing the sheet at any step abandons nothing but the sheet.
 *
 * ## Sign-in is about the donor, check-in is about a room (GH-856)
 *
 * The person icon used to open a check-in picker for a signed-out visitor — "Where are you?" and a
 * list of *other organizations'* events — even on an organization's general-fund page, and a
 * signed-in donor there saw "Event not found" because the page tried to check them in to the
 * general fund, which is not a place anyone can be. Now:
 *
 *  - The profile door always opens on sign-in for a stranger. Never a picker, never anybody's list.
 *  - Check-in happens only where the host says this page *is* a live event (`hereEventId`), and
 *    only after sign-in has already succeeded — a refused check-in never undoes a sign-in, and a
 *    page's passive arrival never shows an error at all.
 *  - The event picker survives only where a host allows it (`allowEventPicker`): the directory and
 *    the event pages, not an organization's or a fund's.
 *  - Confirming the code finishes the job: the host gets the session at once (so the give sheet
 *    can lead with the saved card without a refresh) and the sheet closes on the profile door.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useDialogFocus } from "@givehere/ui";
import type {
  DirectoryEntry,
  DonorSessionResponse,
  EngagementPromptWire,
} from "@givehere/contracts";
import { AdvancedPrivacyStep } from "./AdvancedPrivacy.js";
import { ProfileStep } from "./DonorDetails.js";
import { ReceiptsStep } from "./DonorReceipts.js";
import { useProviderSignIn } from "./provider-sign-in.js";
import {
  completePhoneSignIn,
  DonorApiError,
  storedToken,
  donorCheckIn,
  donorCheckOut,
  donorSignOut,
  fetchDonorSession,
  fetchEngagement,
  forgetToken,
  startDonorSignIn,
  updateDonorProfile,
} from "./donor-session.js";
import { Explain } from "./Explain.js";
import { InviteCodeEntry, InviteRequiredStep, useSignUpAccess } from "./InviteAccess.js";
import { useDonorMembership } from "./membership-hooks.js";
import { isSignUpRefusal, type DoorAttempt } from "./sign-up-access.js";
import { HaveInviteStep, InvitesStep, MembershipSummary } from "./MembershipSteps.js";
/* Lifted out of this file so the event page's expanded action card and this sheet render the
   same four prompt kinds from one implementation (GH-781). Unchanged in the move. */
import { PromptView } from "./PromptView.js";
/* Lifted for the same reason, and used by the same two hosts. */
import { ReactionBar } from "./ReactionBar.js";
/* Lifted for the same reason again: `/c/<eventId>` — the code on the hall wall — asks a stranger
   for the same phone number and the same six digits this sheet does, and a second copy of a
   sign-in form is how one of the two stops accepting the SMS autofill. Unchanged in the move. */
import { CodeStep, SignInChoices } from "./SignInSteps.js";

type Step =
  | { kind: "menu" }
  | { kind: "pick-event" }
  | { kind: "phone"; eventId: string | null }
  | {
      kind: "code";
      eventId: string | null;
      phone: string;
      verificationId: string;
      hint: string | null;
    }
  /** GH-871b: a new person without a usable invite — the code field and the waitlist. */
  | { kind: "invite"; eventId: string | null; attempt: DoorAttempt; refusal: DonorApiError }
  | { kind: "profile" }
  | { kind: "receipts" }
  /** "Edit my details" → "Advanced privacy" (GH-858): never reachable from the first level. */
  | { kind: "advanced-privacy" }
  /** Membership (GH-864e): the member's invites, and a code for somebody who has one. */
  | { kind: "invites" }
  | { kind: "have-invite" }
  | { kind: "prompt"; prompt: EngagementPromptWire };

export function DonorPanel({
  token,
  session,
  view = "profile",
  menuLinks = [],
  previewPrompts = [],
  eventName = null,
  joinEventId = null,
  hereEventId = null,
  entries,
  allowEventPicker = true,
  strangerStartsAt = "sign-in",
  onSession,
  onSignedIn,
  onSignedOut,
  onDismiss,
  onJoinConsumed,
}: {
  /**
   * Whether "Check in at an event" — the picker over the directory's live events — is offered at
   * all (GH-856). False on an organization's or a fund's page: a donor there came for *that*
   * organization, and a list of other organizations' events is somebody else's page.
   */
  allowEventPicker?: boolean;
  /**
   * Where a stranger's tap on the profile door lands. Sign-in everywhere (GH-856) except the
   * directory, whose stranger control is literally labelled "Check in" (ADDENDUM §1's dual
   * control): there the picker over nearby live events is what the label promised, and it carries
   * its own "sign in without checking in" way out.
   */
  strangerStartsAt?: "sign-in" | "pick-event";
  /**
   * Sign-in finished from the profile door. The sheet closes itself right after; the host shows
   * the quiet confirmation ("Signed in as …"). Not called from the hamburger's check-in journey,
   * which stays open on the room the donor was trying to join.
   */
  onSignedIn?: (session: DonorSessionResponse) => void;
  /**
   * Which face of the sheet opened: "profile" is the person icon (who you are, where you are
   * checked in, your details), "menu" is the hamburger (what this event is running, plus the
   * site links). One sheet, two doors — the sign-in journey behind them is shared.
   */
  view?: "profile" | "menu";
  /**
   * Page-specific rows for the menu's link section — the event page passes its "Big screen"
   * and "Find another event" links here now that nothing sits at the bottom of that page.
   */
  menuLinks?: readonly { href: string; label: string; external?: boolean }[];
  /**
   * What this page's event is running, shown in the menu before check-in so the polls and
   * questions are discoverable — tapping one starts the check-in that participation needs.
   */
  previewPrompts?: readonly EngagementPromptWire[];
  /** Names the event in the menu's check-in invitation. */
  eventName?: string | null;
  token: string | null;
  session: DonorSessionResponse | null;
  /** Set when a "join the room" link opened this sheet: skip the picker, land on this event. */
  joinEventId?: string | null;
  /**
   * The event page this sheet opened from. Quieter than a join link: it fills an *empty*
   * check-in and pre-selects the event for sign-in, but never moves a donor who is already
   * checked in somewhere else — reopening this sheet must not undo a switch they made.
   */
  hereEventId?: string | null;
  /**
   * Called once a signed-in donor's arrival (join link or event page) has been acted on, so
   * the owner stops passing it back on the next open. An arrival is spent exactly once —
   * without this, every remount of the sheet would re-run it and drag the donor back to the
   * arrival event forever, overriding any switch or check-out they chose in between.
   */
  onJoinConsumed?: () => void;
  /** The directory rows the home page is already showing — the event picker reuses them. */
  entries: readonly DirectoryEntry[];
  onSession: (token: string, session: DonorSessionResponse) => void;
  onSignedOut: () => void;
  onDismiss: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const signedIn = token !== null && session !== null;
  // Membership (GH-864e): read only on the profile face, and `null` — nothing drawn — when gated off.
  const { membership, setMembership } = useDonorMembership(
    signedIn && view === "profile" ? token : null,
  );
  const arrivalEventId = joinEventId ?? hereEventId;
  const [step, setStep] = useState<Step>(() => {
    // The hamburger menu has content for everyone — the site links at minimum — so it always
    // opens on the menu; check-in is an invitation inside it, never a wall in front of it.
    if (view === "menu") return { kind: "menu" };
    if (signedIn) return { kind: "menu" };
    // A stranger at the profile door is offered sign-in — never a picker, except behind the
    // directory's own "Check in" control (GH-856). An arrival's event rides along so a live room
    // checks them in once they are in.
    if (strangerStartsAt === "pick-event" && arrivalEventId === null && allowEventPicker) {
      return { kind: "pick-event" };
    }
    return { kind: "phone", eventId: arrivalEventId };
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set the moment a confirm succeeds, so a second press can never send the spent code again. */
  const confirmed = useRef(false);

  const fail = (caught: unknown, fallback: string) =>
    setError(caught instanceof DonorApiError ? caught.message : fallback);

  // GH-871b: the invite code a new person signs up with, on every door, while membership is on.
  const access = useSignUpAccess({ enabled: !signedIn });
  /** The door the person pressed, for the invite step's retry; set as the door is pressed. */
  const pendingEventId = useRef<string | null>(null);
  const refused = (attempt: DoorAttempt, refusal: DonorApiError) => {
    setError(null);
    setStep({ kind: "invite", eventId: pendingEventId.current, attempt, refusal });
  };

  // Which doors exist (GH-856, GH-859, GH-866): the server's options for this origin, on every
  // browser. A failed read just leaves the phone, which is always the door.
  const providers = useProviderSignIn({
    enabled: !signedIn,
    setBusy,
    setError,
    signUpAccess: access.fields,
    onSignUpRefused: refused,
  });

  /**
   * After the server has issued a session, by whichever door: hand it to the host at once, then
   * check in where this page is a live room. A refused check-in never undoes the sign-in — the
   * donor is in, and the room is optional (GH-856: "Event not found" used to strand them here).
   */
  const finishSignIn = async (issued: DonorSessionResponse, eventId: string | null) => {
    onSession(issued.token, issued);
    let current = issued;
    if (eventId) {
      try {
        current = { ...(await donorCheckIn(issued.token, eventId)), token: issued.token };
        onSession(issued.token, current);
      } catch {
        // Presence is optional; the sign-in already stands.
      }
    }
    if (view === "menu") {
      // The hamburger's journey was "let me take part here": stay open on the room.
      setStep({ kind: "menu" });
      return;
    }
    onSignedIn?.(current);
    onDismiss();
  };

  // A remembered donor following a join link: presence is the point of the tap, so check in
  // right away rather than making them find the event in a picker they already chose from.
  // An event page is only allowed to fill an empty check-in, never to overwrite one — and a
  // page's passive arrival never shows an error (GH-856): the donor did not ask for it.
  useEffect(() => {
    if (token === null || session === null) return;
    if (joinEventId || hereEventId) onJoinConsumed?.();
    const target = joinEventId ?? (session.checkIn ? null : hereEventId);
    if (!target || session.checkIn?.eventId === target) return;
    const explicit = joinEventId !== null;
    setBusy(true);
    donorCheckIn(token, target)
      .then((updated) => onSession(token, updated))
      .catch((caught: unknown) => {
        if (explicit) fail(caught, "Could not check you in.");
      })
      .finally(() => setBusy(false));
    // Runs once at open: this is the arrival, not a subscription to prop changes.
  }, []);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="donor-title">
      <div className="sheet__panel sheet__panel--tall lattice-edge" ref={panelRef} tabIndex={-1}>
        <button type="button" className="sheet__close" onClick={onDismiss} aria-label="Close">
          &#x2715;
        </button>

        {step.kind === "menu" && view === "profile" && signedIn && (
          <ProfileMenu
            session={session}
            offerCheckIn={allowEventPicker}
            membership={
              <MembershipSummary
                token={token}
                membership={membership}
                onChange={setMembership}
                onInvites={() => setStep({ kind: "invites" })}
                onHaveInvite={() => setStep({ kind: "have-invite" })}
              />
            }
            onReceipts={() => setStep({ kind: "receipts" })}
            onPickEvent={() => setStep({ kind: "pick-event" })}
            onCheckOut={() => {
              const where = session.checkIn;
              if (!token || !where) return;
              setError(null);
              setBusy(true);
              donorCheckOut(token, where.eventId)
                .then((updated) => onSession(token, updated))
                .catch((caught: unknown) => fail(caught, "Could not check you out."))
                .finally(() => setBusy(false));
            }}
            onEditProfile={() => setStep({ kind: "profile" })}
            onSignOut={() => {
              if (token) void donorSignOut(token).catch(() => undefined);
              forgetToken();
              onSignedOut();
            }}
          />
        )}

        {step.kind === "menu" && view === "menu" && (
          <EventMenu
            token={token}
            session={session}
            links={menuLinks}
            previewPrompts={previewPrompts}
            eventName={eventName}
            offerCheckIn={arrivalEventId !== null || allowEventPicker}
            onPickEvent={() => {
              // On an event page a signed-out visitor already chose their event by being
              // here — the phone form is next, not a picker.
              if (!signedIn && arrivalEventId) {
                setStep({ kind: "phone", eventId: arrivalEventId });
              } else {
                setStep({ kind: "pick-event" });
              }
            }}
            onOpenPrompt={(prompt) => setStep({ kind: "prompt", prompt })}
          />
        )}

        {step.kind === "pick-event" && (
          <EventPicker
            entries={entries}
            busy={busy}
            onSignIn={signedIn ? null : () => setStep({ kind: "phone", eventId: null })}
            onPick={(eventId) => {
              setError(null);
              if (token) {
                // Already remembered: presence is one tap.
                setBusy(true);
                donorCheckIn(token, eventId)
                  .then((updated) => {
                    onSession(token, updated);
                    setStep({ kind: "menu" });
                  })
                  .catch((caught: unknown) => fail(caught, "Could not check you in."))
                  .finally(() => setBusy(false));
              } else {
                setStep({ kind: "phone", eventId });
              }
            }}
          />
        )}

        {step.kind === "phone" && (
          <SignInChoices
            busy={busy}
            appleOffered={providers.appleOffered}
            onApple={() => {
              pendingEventId.current = step.eventId;
              void providers.signInWithApple((issued) => finishSignIn(issued, step.eventId));
            }}
            googleOffered={providers.googleOffered}
            onGoogle={() => {
              pendingEventId.current = step.eventId;
              void providers.signInWithGoogle((issued) => finishSignIn(issued, step.eventId));
            }}
            footer={<InviteCodeEntry access={access} />}
            onPhone={(phone) => {
              setError(null);
              setBusy(true);
              startDonorSignIn(phone)
                .then((started) =>
                  setStep({
                    kind: "code",
                    eventId: step.eventId,
                    phone,
                    verificationId: started.verificationId,
                    hint: started.developmentCode,
                  }),
                )
                .catch((caught: unknown) => fail(caught, "Could not send a code."))
                .finally(() => setBusy(false));
            }}
          />
        )}

        {step.kind === "code" && (
          <CodeStep
            hint={step.hint}
            busy={busy}
            onSubmit={(code) => {
              // One confirm per code. A second press while the first is in flight, or after it
              // landed, would send a spent code and come back "already used" (GH-856).
              if (confirmed.current) return;
              confirmed.current = true;
              setError(null);
              setBusy(true);
              const hadToken = storedToken();
              const attempt = {
                door: "phone" as const,
                verificationId: step.verificationId,
                code,
                phone: step.phone,
              };
              pendingEventId.current = step.eventId;
              completePhoneSignIn(
                {
                  verificationId: step.verificationId,
                  code,
                  phone: step.phone,
                  ...access.fields(),
                },
                hadToken,
              )
                .then((issued) => finishSignIn(issued, step.eventId))
                .catch((caught: unknown) => {
                  // A wrong code may be retyped; only success spends the press.
                  confirmed.current = false;
                  if (isSignUpRefusal(caught)) refused(attempt, caught);
                  else fail(caught, "That code is not correct.");
                })
                .finally(() => setBusy(false));
            }}
          />
        )}

        {step.kind === "invite" && (
          <InviteRequiredStep
            attempt={step.attempt}
            refusal={step.refusal}
            access={access}
            headingId="donor-title"
            onIssued={(issued) => finishSignIn(issued, step.eventId)}
            onRestart={() => setStep({ kind: "phone", eventId: step.eventId })}
          />
        )}

        {step.kind === "receipts" && signedIn && (
          <ReceiptsStep token={token} onBack={() => setStep({ kind: "menu" })} />
        )}

        {step.kind === "profile" && signedIn && (
          <ProfileStep
            session={session}
            token={token}
            busy={busy}
            onSave={(details) => {
              setError(null);
              if (!details.displayName && !details.email && !details.postalAddress) {
                // Nothing entered is a legitimate choice, not a validation failure.
                setStep({ kind: "menu" });
                return;
              }
              setBusy(true);
              updateDonorProfile(token, details)
                // Refresh the session so the menu greets the donor by the name they just saved,
                // rather than by the phone digits from the load before it existed.
                .then(() => fetchDonorSession(token))
                .then((refreshed) => {
                  onSession(token, refreshed);
                  setStep({ kind: "menu" });
                })
                .catch((caught: unknown) => fail(caught, "Could not save that."))
                .finally(() => setBusy(false));
            }}
            onSkip={() => setStep({ kind: "menu" })}
            onAdvancedPrivacy={() => {
              setError(null);
              setStep({ kind: "advanced-privacy" });
            }}
          />
        )}

        {step.kind === "advanced-privacy" && signedIn && (
          <AdvancedPrivacyStep token={token} onBack={() => setStep({ kind: "profile" })} />
        )}

        {step.kind === "invites" && signedIn && membership && (
          <InvitesStep
            token={token}
            membership={membership}
            onChange={setMembership}
            onBack={() => setStep({ kind: "menu" })}
          />
        )}

        {step.kind === "have-invite" && signedIn && (
          <HaveInviteStep
            token={token}
            onChange={setMembership}
            onBack={() => setStep({ kind: "menu" })}
          />
        )}

        {step.kind === "prompt" && signedIn && (
          <PromptView
            token={token}
            prompt={step.prompt}
            onBack={() => setStep({ kind: "menu" })}
            eventId={session.checkIn?.eventId ?? null}
          />
        )}

        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- menu */

/**
 * The person icon: who this device belongs to and where they are checked in. Everything about
 * the donor themself lives here; what the event is running lives in the hamburger EventMenu.
 */
function ProfileMenu({
  session,
  offerCheckIn,
  membership,
  onReceipts,
  onPickEvent,
  onCheckOut,
  onEditProfile,
  onSignOut,
}: {
  session: DonorSessionResponse;
  /** False on an organization's or a fund's page: no picker over other organizations' events. */
  offerCheckIn: boolean;
  /** The membership block (GH-864e); renders nothing while membership is off. */
  membership: ReactNode;
  onReceipts: () => void;
  onPickEvent: () => void;
  onCheckOut: () => void;
  onEditProfile: () => void;
  onSignOut: () => void;
}) {
  const checkIn = session.checkIn;

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        {donorName(session)}
      </h2>

      {checkIn ? (
        <p className="donor__where">
          Checked in at <strong>{checkIn.eventName}</strong> · {checkIn.organizationDisplayName}
        </p>
      ) : offerCheckIn ? (
        <p className="donor__where">Not checked in anywhere yet.</p>
      ) : (
        <p className="donor__where">Signed in on this device.</p>
      )}

      {membership}

      <div className="donor__actions">
        {/* Receipts first, as the iPhone's Account screen leads with them (handoff F7). */}
        <button type="button" className="link" onClick={onReceipts}>
          Receipts
        </button>
        {offerCheckIn && (
          <button type="button" className="link" onClick={onPickEvent}>
            {checkIn ? "Check in somewhere else" : "Check in at an event"}
          </button>
        )}
        {checkIn && (
          <button type="button" className="link" onClick={onCheckOut}>
            Check out of {checkIn.eventName}
          </button>
        )}
        <button type="button" className="link" onClick={onEditProfile}>
          Edit my details
        </button>
        <button type="button" className="link" onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </>
  );
}

/**
 * How the sheet names its own donor: the name they chose, else the last four of their number,
 * else plainly "Signed in" (an Apple-only donor with no name has neither). Their own data, shown
 * to them after sign-in — never before.
 */
export function donorName(session: DonorSessionResponse): string {
  if (session.profile.displayName) return session.profile.displayName;
  if (session.profile.phoneLast4) return `Phone ···${session.profile.phoneLast4}`;
  return "Signed in";
}

/**
 * The hamburger icon: what the checked-in event is running — reactions, polls, questions,
 * the comment wall — plus the quiet site links that used to live only in the page footer.
 * It renders something for every state: a signed-out visitor still gets the links and an
 * invitation to check in, never a wall.
 */
function EventMenu({
  token,
  session,
  links,
  previewPrompts,
  eventName,
  offerCheckIn,
  onPickEvent,
  onOpenPrompt,
}: {
  token: string | null;
  session: DonorSessionResponse | null;
  links: readonly { href: string; label: string; external?: boolean }[];
  previewPrompts: readonly EngagementPromptWire[];
  eventName: string | null;
  /** False where this page is no room and no picker is allowed (GH-856): links only. */
  offerCheckIn: boolean;
  onPickEvent: () => void;
  onOpenPrompt: (prompt: EngagementPromptWire) => void;
}) {
  const [prompts, setPrompts] = useState<readonly EngagementPromptWire[] | null>(null);
  const checkIn = session?.checkIn ?? null;

  useEffect(() => {
    if (!checkIn) return;
    let cancelled = false;
    fetchEngagement(checkIn.eventId)
      .then((loaded) => {
        if (!cancelled) setPrompts(loaded);
      })
      .catch(() => {
        if (!cancelled) setPrompts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [checkIn]);

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        {checkIn ? checkIn.eventName : (eventName ?? "Menu")}
      </h2>

      {checkIn ? (
        <p className="donor__where">{checkIn.organizationDisplayName}</p>
      ) : offerCheckIn ? (
        <Explain className="donor__where" label="Check in to take part" title="Checking in">
          <p>
            {eventName
              ? `Check in at ${eventName} to react and take part.`
              : "Check in at an event to react and take part."}
          </p>
        </Explain>
      ) : null}

      {token && checkIn && <ReactionBar token={token} eventId={checkIn.eventId} />}

      {checkIn && prompts === null && <p className="notice">Loading…</p>}

      {checkIn && prompts !== null && prompts.length === 0 && (
        <p className="notice">{checkIn.organizationDisplayName} has nothing running right now.</p>
      )}

      {checkIn && prompts !== null && prompts.length > 0 && (
        <ul className="donor__options">
          {prompts.map((prompt) => (
            <li key={prompt.id}>
              <button type="button" className="donor__option" onClick={() => onOpenPrompt(prompt)}>
                <span>{promptLabel(prompt)}</span>
                <span className="donor__option-title">{prompt.title}</span>
                {prompt.status === "closed" && <span className="donor__closed">closed</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!checkIn && offerCheckIn && previewPrompts.length > 0 && (
        <ul className="donor__options">
          {previewPrompts.map((prompt) => (
            <li key={prompt.id}>
              {/* Discoverable before check-in; the tap starts the check-in participation needs. */}
              <button type="button" className="donor__option" onClick={onPickEvent}>
                <span>{promptLabel(prompt)}</span>
                <span className="donor__option-title">{prompt.title}</span>
                {prompt.status === "closed" && <span className="donor__closed">closed</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!checkIn && offerCheckIn && (
        <div className="donor__actions">
          <button type="button" className="link" onClick={onPickEvent}>
            {eventName ? `Check in at ${eventName}` : "Check in at an event"}
          </button>
        </div>
      )}

      <nav className="donor__links" aria-label="More on GiveHere">
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {link.label}
          </a>
        ))}
        <a href="/org">List your organization</a>
        <a href="/console">Organization console</a>
      </nav>
    </>
  );
}

const promptLabel = (prompt: EngagementPromptWire): string =>
  prompt.kind === "raffle"
    ? "🎟 Raffle"
    : prompt.kind === "poll"
      ? "📊 Poll"
      : prompt.kind === "question"
        ? "❓ Ask a question"
        : "💬 Comment wall";

/* ---------------------------------------------------------------- pick event */

function EventPicker({
  entries,
  busy,
  onSignIn,
  onPick,
}: {
  entries: readonly DirectoryEntry[];
  busy: boolean;
  /** A stranger's way past the picker to plain sign-in (GH-856); null once signed in. */
  onSignIn: (() => void) | null;
  onPick: (eventId: string) => void;
}) {
  // Presence needs a live event; an organization with nothing running is not a place to check in.
  const candidates = entries.filter((entry) => entry.liveEventId !== null);

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        Where are you?
      </h2>
      <Explain className="donor__where" label="Check in to see more" title="What check-in shows">
        <p>Check in to see what this event is running.</p>
      </Explain>

      {candidates.length === 0 && (
        <p className="notice">
          No events found nearby. Search for your organization on the home screen first.
        </p>
      )}

      <ul className="donor__options">
        {candidates.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              className="donor__option"
              disabled={busy}
              onClick={() => entry.liveEventId && onPick(entry.liveEventId)}
            >
              <span className="donor__option-title">{entry.displayName}</span>
              <span className="donor__closed">{entry.liveEventName}</span>
            </button>
          </li>
        ))}
      </ul>

      {onSignIn && (
        <div className="donor__actions">
          <button type="button" className="link" onClick={onSignIn}>
            Sign in without checking in
          </button>
        </div>
      )}
    </>
  );
}
